import { duracionMs, nivelEn, type EnvolventeAudio } from '@alter/api-client';

/**
 * Capa de habla del avatar (tanda 4, D-4).
 *
 * Mueve la boca con el nivel del audio que está sonando, **sin fonemas ni
 * visemas**: no se analiza el texto. El nivel llega precalculado en una
 * `EnvolventeAudio` (`@alter/api-client`); acá se lo sigue con su reloj,
 * se le aplica compuerta y envolvente de ataque/caída, y se traduce a los
 * cuatro blend shapes de boca que `07_BLENDSHAPES_ARKIT.md` §4.2 reserva
 * para esto.
 *
 * Es hermana de la capa idle: produce desplazamientos y **no toca el
 * rig**; quien los compone es el `ThreeRigAdapter`. `avatar-core` no se
 * entera de que existe, y `EXPRESIONES_CONFIG` no cambia — el habla no es
 * una expresión, es algo que le pasa a cualquier expresión.
 */

/** Pesos de boca que aporta el habla en un instante. */
export interface HablaOffsets {
  /** `jawOpen`: el canal principal. */
  mandibula: number;
  /** `mouthFunnel`: boca en "o". */
  embudo: number;
  /** `mouthPucker`: labios fruncidos. */
  fruncido: number;
  /** `mouthClose`: labios juntos en las pausas. */
  cierre: number;
  /** Nivel ya suavizado, `0..1`. Lo usa el adapter para atenuar la boca. */
  nivel: number;
}

export const HABLA_SILENCIO: HablaOffsets = {
  mandibula: 0,
  embudo: 0,
  fruncido: 0,
  cierre: 0,
  nivel: 0,
};

/**
 * Constantes de la capa. Se pueden sobrescribir para calibrar desde
 * `/dev/avatar`; los valores por defecto son los del producto y los que
 * la tanda 5 va a revisar por avatar.
 */
export interface HablaOpts {
  /** Multiplica el nivel de la envolvente antes de la compuerta. */
  ganancia?: number;
  /** Por debajo de este nivel, la boca se considera en silencio. */
  compuerta?: number;
  /** Constante de tiempo al subir. Corta: la boca abre con la sílaba. */
  ataqueMs?: number;
  /** Constante de tiempo al bajar. Más larga: evita el temblor. */
  caidaMs?: number;
  /** Apertura máxima de `jawOpen`. */
  mandibulaMax?: number;
  /** Peso máximo de `mouthFunnel` / `mouthPucker`. */
  formaMax?: number;
  /** Peso de `mouthClose` en las pausas entre frases. */
  cierreMax?: number;
  /**
   * Nivel por debajo del cual se considera que hay pausa y los labios se
   * juntan. Por encima, `mouthClose` es **cero**.
   */
  cierreUmbral?: number;
  /** Período de la variación lenta entre embudo y fruncido. */
  formaPeriodoMs?: number;
}

export const HABLA_DEFECTOS: Required<HablaOpts> = {
  ganancia: 1.15,
  /**
   * 0,08 sobre un nivel normalizado al pico del clip. Más bajo y la boca
   * tiembla con la respiración y el ruido de fondo del TTS; más alto y se
   * come el arranque de las frases suaves.
   */
  compuerta: 0.08,
  ataqueMs: 35,
  caidaMs: 110,
  /**
   * 0,55 y no 1: `jawOpen` en 1 es un bostezo. Una conversación normal
   * abre la mandíbula bastante menos (P-2).
   */
  mandibulaMax: 0.55,
  formaMax: 0.22,
  /**
   * El cierre de labios entre frases es sutil: `mouthClose` sube el labio
   * inferior contra el superior, y con la mandíbula abierta se monta uno
   * sobre otro. Revisado a ojo el 2026-09-19: bajó de 0,25 a 0,12.
   */
  cierreMax: 0.12,
  cierreUmbral: 0.15,
  /**
   * Períodos largos: la forma de la boca cambia entre frases, no entre
   * sílabas. Con un período corto parece que mastica.
   */
  formaPeriodoMs: 2600,
};

/**
 * Sigue una envolvente y entrega los pesos de boca de cada frame.
 *
 * El reloj lo lleva la propia capa, avanzada desde el render loop con el
 * mismo `deltaMs` que el resto del adapter: así la boca va al ritmo de
 * los frames que se dibujan.
 */
export class CapaHabla {
  private opts: Required<HablaOpts>;
  private envolvente: EnvolventeAudio | null = null;
  /** Posición dentro del clip, en ms. */
  private tMs = 0;
  /** Tiempo total, para la variación lenta de forma. */
  private faseMs = 0;
  private nivelSuavizado = 0;

  constructor(opts: HablaOpts = {}) {
    this.opts = { ...HABLA_DEFECTOS, ...opts };
  }

  configurar(opts: HablaOpts): void {
    this.opts = { ...this.opts, ...opts };
  }

  /**
   * Empieza a seguir una envolvente.
   *
   * @param desfaseMs cuánto lleva sonando el audio. La envolvente se
   *   calcula mientras el audio ya arrancó, así que la boca entra en el
   *   punto que corresponde en vez de desde el principio.
   */
  iniciar(envolvente: EnvolventeAudio, desfaseMs = 0): void {
    this.envolvente = envolvente;
    this.tMs = Math.max(0, desfaseMs);
  }

  /** Corta el habla. El nivel decae con la constante de caída. */
  detener(): void {
    this.envolvente = null;
  }

  /**
   * Nivel por debajo del cual hay pausa. Lo usan también los gestos
   * (tanda 8), para que la boca y la cabeza vean la misma pausa.
   */
  get umbralPausa(): number {
    return this.opts.cierreUmbral;
  }

  get hablando(): boolean {
    return this.envolvente !== null && this.tMs < duracionMs(this.envolvente);
  }

  /** Avanza el reloj y devuelve los pesos de este frame. */
  update(deltaMs: number): HablaOffsets {
    this.faseMs += deltaMs;
    const crudo = this.envolvente ? nivelEn(this.envolvente, this.tMs) * this.opts.ganancia : 0;
    if (this.envolvente) this.tMs += deltaMs;

    // Compuerta de ruido con reescalado: sin reescalar, el nivel salta de
    // 0 a `compuerta` al abrir y la boca da un tirón.
    const { compuerta } = this.opts;
    const puerta = crudo <= compuerta ? 0 : Math.min(1, (crudo - compuerta) / (1 - compuerta));

    // Seguidor de envolvente: sube rápido, baja lento. Exponencial, así
    // el resultado no depende de la tasa de frames.
    const tau = puerta > this.nivelSuavizado ? this.opts.ataqueMs : this.opts.caidaMs;
    const k = 1 - Math.exp(-deltaMs / Math.max(1, tau));
    this.nivelSuavizado += (puerta - this.nivelSuavizado) * k;
    if (this.nivelSuavizado < 1e-4) this.nivelSuavizado = 0;

    return this.pesos(this.nivelSuavizado);
  }

  /** Traducción nivel → pesos. Pura: expuesta para los tests. */
  private pesos(nivel: number): HablaOffsets {
    if (nivel === 0) return HABLA_SILENCIO;
    const { mandibulaMax, formaMax, cierreMax, cierreUmbral, formaPeriodoMs } = this.opts;

    // Un solo canal de apertura se lee como una mandíbula que rebota. La
    // forma de los labios varía lento entre embudo y fruncido, que es lo
    // que hace una boca real al pasar de unas vocales a otras.
    //
    // **Se alternan, no se mezclan.** Aplicando los dos a la vez a media
    // amplitud los labios se engrosan y el inferior se monta sobre el
    // superior (visto a ojo el 2026-09-19). Con media onda rectificada,
    // en el cruce los dos valen cero y sólo uno manda por vez.
    const onda = Math.sin((this.faseMs / formaPeriodoMs) * Math.PI * 2);

    return {
      mandibula: nivel * mandibulaMax,
      embudo: nivel * formaMax * Math.max(0, onda),
      fruncido: nivel * formaMax * Math.max(0, -onda),
      // `mouthClose` sólo en las pausas de verdad, con el clip todavía
      // sonando. Mientras hay voz es cero: juntar los labios mientras la
      // mandíbula se abre es justo lo que se veía mal.
      cierre: this.hablando && nivel < cierreUmbral ? (1 - nivel / cierreUmbral) * cierreMax : 0,
      nivel,
    };
  }
}
